import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { StarIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	FlexGap,
	Heading,
	Jumbo,
	Paragraph,
	Pill,
	QuillText,
	Separator,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import { sortByOrderThenTitle } from '../competencies/sortJobCompetenciesByOrder';
import { KeyPositionToggle } from './KeyPositionToggle';
import { useJobTitleName } from './useJobTitleName';

const CompetencyItem = styled(Box)`
	&:not(:last-child) {
		border-bottom: 1px solid ${tokens.border.subtle};
	}
`;

function hasJobSpecificDescription(jobCompetency: graphql.JobCompetencyFragment) {
	return (jobCompetency.description?.length ?? 0) > 0;
}

function preferJobCompetency(
	current: graphql.JobCompetencyFragment,
	candidate: graphql.JobCompetencyFragment,
) {
	const currentHasDescription = hasJobSpecificDescription(current);
	const candidateHasDescription = hasJobSpecificDescription(candidate);

	if (currentHasDescription !== candidateHasDescription) {
		return candidateHasDescription ? candidate : current;
	}

	if (!!current.level !== !!candidate.level) {
		return candidate.level ? candidate : current;
	}

	return current;
}

function uniqueJobCompetencies(items: graphql.JobCompetencyFragment[]) {
	const byCompetencyID = new Map<string, graphql.JobCompetencyFragment>();

	for (const item of items) {
		const existing = byCompetencyID.get(item.competency.id);

		byCompetencyID.set(item.competency.id, existing ? preferJobCompetency(existing, item) : item);
	}

	return [...byCompetencyID.values()];
}

function jobCompetencyDescription(
	jobCompetency: graphql.JobCompetencyFragment,
	localize: ReturnType<typeof useLocalize>['localize'],
) {
	const jobSpecific = localize(jobCompetency.description ?? [])?.text;

	if (!QuillText.isEmpty(jobSpecific)) {
		return jobSpecific;
	}

	const competencyDescription = localize(jobCompetency.competency.description ?? [])?.text;

	if (!QuillText.isEmpty(competencyDescription)) {
		return competencyDescription;
	}

	return undefined;
}

export default function JobPosition() {
	const { params } = useRouteMatch<{ id: string }>();
	const { localize } = useLocalize();
	const jobTitleName = useJobTitleName();
	const { t } = useTranslation();

	const hasSuccession = useFeature(graphql.FeatureName.Succession);
	const query = graphql.useJobPositionQuery({ variables: { id: params.id } });

	const competencies = useMemo(() => {
		const items = query.data?.jobPosition.competencies ?? [];

		return sortByOrderThenTitle(
			uniqueJobCompetencies(items),
			(item) => item.order,
			(item) => localize(item.competency.title).text,
		);
	}, [localize, query.data?.jobPosition.competencies]);

	return (
		<>
			{!query.data && (
				<>
					<Jumbo.Skeleton wide />
					<Separator />
					<Box m={4}>
						<Heading.Skeleton mb={2} />
						<Paragraph.Skeleton />
					</Box>
				</>
			)}

			{query.data && (
				<>
					<Jumbo
						wide
						title={jobTitleName(query.data.jobPosition.jobTitle)}
						footer={<Separator />}
					/>
					<Box m={4}>
						{hasSuccession && query.data.jobPosition.jobTitle.isKeyPosition && (
							<Box mb={3}>
								<Pill tint="warning" small>
									<StarIcon />
									{t('jobPosition.keyPosition.label')}
								</Pill>
							</Box>
						)}
						{hasSuccession && (
							<Box mb={4}>
								<KeyPositionToggle jobTitle={query.data.jobPosition.jobTitle} />
							</Box>
						)}
						<Heading>{t('jobPosition.details.title.heading')}</Heading>

						<QuillText
							value={localize(query.data.jobPosition.jobTitle.description ?? [])?.text}
							placeholder={t('jobPosition.details.description.empty')}
						/>

						{competencies.length > 0 && (
							<Box mt={4}>
								<Heading mb={2}>{t('jobPosition.details.competencies.heading')}</Heading>
								{competencies.map((jobCompetency) => {
									const description = jobCompetencyDescription(jobCompetency, localize);
									const labelTitle = jobCompetency.label
										? localize(jobCompetency.label.title).text
										: undefined;

									return (
										<CompetencyItem key={jobCompetency.id} py={3}>
											<FlexGap gap={8} alignItems="center">
												<Subheading>{localize(jobCompetency.competency.title).text}</Subheading>
												{labelTitle && <Pill small>{labelTitle}</Pill>}
											</FlexGap>
											{description && (
												<Box mt={2}>
													<QuillText value={description} />
												</Box>
											)}
										</CompetencyItem>
									);
								})}
							</Box>
						)}
					</Box>
				</>
			)}
		</>
	);
}
